import type { Bounds, Camera, Size, Vec2 } from '@/types';
import { cameraFittingBounds, clampZoom, zoomCameraAt } from '@/core/coordinates/coordinateSystem';

/**
 * Higher-level camera movements built on the coordinate primitives. All functions
 * are pure: they take a camera and return a new one, so they are trivially testable
 * and can be animated by interpolating between the inputs and outputs.
 */

/** Screen-space centre of the viewport. */
export const viewportCenter = (viewport: Size): Vec2 => ({ x: viewport.width / 2, y: viewport.height / 2 });

/** World point currently at the centre of the viewport. */
export const cameraCenterWorld = (camera: Camera, viewport: Size): Vec2 => ({
  x: camera.position.x + viewport.width / (2 * camera.zoom),
  y: camera.position.y + viewport.height / (2 * camera.zoom),
});

/** Camera that places `world` at the viewport centre, optionally at a new zoom. */
export const centerCameraOn = (world: Vec2, viewport: Size, zoom: number): Camera => {
  const z = clampZoom(zoom);
  return {
    zoom: z,
    position: { x: world.x - viewport.width / (2 * z), y: world.y - viewport.height / (2 * z) },
  };
};

/** Camera centred on the middle of `bounds`, keeping the current zoom. */
export const centerCameraOnBounds = (camera: Camera, bounds: Bounds, viewport: Size): Camera =>
  centerCameraOn({ x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 }, viewport, camera.zoom);

export interface FitOptions {
  readonly padding?: number;
  /** Never zoom in past this when fitting small content (avoids a giant single note). */
  readonly maxZoom?: number;
}

/** Camera showing all of `bounds`, padded, without zooming in past `maxZoom`. */
export const fitCameraToBounds = (bounds: Bounds, viewport: Size, options: FitOptions = {}): Camera => {
  const fitted = cameraFittingBounds(bounds, viewport, options.padding ?? 64);
  const cap = options.maxZoom ?? 1;
  if (fitted.zoom <= cap) return fitted;
  return centerCameraOn({ x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 }, viewport, cap);
};

/** Multiplies zoom about the viewport centre (keyboard / button zoom). */
export const zoomCameraBy = (camera: Camera, factor: number, viewport: Size): Camera =>
  zoomCameraAt(camera, camera.zoom * factor, viewportCenter(viewport));

/** Sets zoom to exactly `zoom` about the viewport centre. */
export const setCameraZoom = (camera: Camera, zoom: number, viewport: Size): Camera =>
  zoomCameraAt(camera, zoom, viewportCenter(viewport));

/** Union of a list of bounds, or null when empty. */
export const unionBounds = (list: readonly Bounds[]): Bounds | null => {
  let acc: Bounds | null = null;
  for (const b of list) {
    acc = acc
      ? {
          minX: Math.min(acc.minX, b.minX),
          minY: Math.min(acc.minY, b.minY),
          maxX: Math.max(acc.maxX, b.maxX),
          maxY: Math.max(acc.maxY, b.maxY),
        }
      : b;
  }
  return acc;
};

/** True when two cameras are visually identical (avoids no-op store updates). */
export const camerasEqual = (a: Camera, b: Camera): boolean =>
  a.zoom === b.zoom && a.position.x === b.position.x && a.position.y === b.position.y;
